import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AdminReview,
  ReviewModerationStatus,
  ReviewsAPI,
} from "../../api/reviews";
import { assetUrl } from "../../api/http";
import { ProductsAPI } from "../../api/products";
import type { Product } from "../../types/products";

type ReviewRewardSettings = {
  enabled: boolean;
  discountType: "percentage" | "fixed";
  discountValue: string;
  allProducts: boolean;
  applicableProductIds: string[];
};

const defaultRewardSettings = (productId?: string): ReviewRewardSettings => ({
  enabled: true,
  discountType: "percentage",
  discountValue: "10",
  allProducts: true,
  applicableProductIds: productId ? [productId] : [],
});

const pretty = (value: string) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (word) => word[0].toUpperCase() + word.slice(1));

const statusPillColor: Record<string, { bg: string; text: string }> = {
  pending: { bg: "#fef9c3", text: "#854d0e" },
  approved: { bg: "#dcfce7", text: "#166534" },
  rejected: { bg: "#fee2e2", text: "#991b1b" },
};

const filterOptions: Array<{
  value: ReviewModerationStatus | "";
  label: string;
}> = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "", label: "All" },
];

const categoryOptions = [
  { value: "", label: "All Products" },
  { value: "pendant", label: "Pendants" },
  { value: "license", label: "License Plates" },
  { value: "tshirt", label: "T-Shirts" },
  { value: "phone", label: "Phone Cases" },
  { value: "cup", label: "Cups/Glasses" },
  { value: "wallet", label: "Wallets" },
];

export default function ReviewsModerationPage() {
  const [items, setItems] = useState<AdminReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ReviewModerationStatus | "">("pending");
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [notesById, setNotesById] = useState<Record<string, string>>({});
  const [rewardById, setRewardById] = useState<Record<string, ReviewRewardSettings>>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [processing, setProcessing] = useState<{
    id: string;
    decision: "approved" | "rejected";
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ReviewsAPI.listAdmin({
        page: 1,
        limit: 60,
        status,
        q: query.trim() || undefined,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to load reviews."
      );
    } finally {
      setLoading(false);
    }
  }, [query, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;
    ProductsAPI.list({ page: 1, limit: 250, includeInactive: true })
      .then((data) => {
        if (!mounted) return;
        setProducts(Array.isArray(data.items) ? data.items : []);
      })
      .catch(() => {
        if (mounted) setProducts([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    if (!category) return items;
    return items.filter((item) => {
      const slug = String(item.productSlug || "").toLowerCase();
      const name = String(item.productName || "").toLowerCase();
      return slug.includes(category) || name.includes(category);
    });
  }, [items, category]);

  const totals = useMemo(() => {
    const pending = filteredItems.filter((item) => item.moderationStatus === "pending").length;
    const flagged = filteredItems.filter(
      (item) => Array.isArray(item.moderationFlags) && item.moderationFlags.length > 0
    ).length;
    return { pending, flagged, total: filteredItems.length };
  }, [filteredItems]);

  const moderate = async (
    item: AdminReview,
    decision: "approved" | "rejected"
  ) => {
    setProcessing({ id: item.id, decision });
    setError(null);
    try {
      const result = await ReviewsAPI.moderate(item.id, {
        action: decision === "approved" ? "approve" : "reject",
        notes: notesById[item.id]?.trim() || undefined,
        reviewReward:
          decision === "approved"
            ? (() => {
                const settings =
                  rewardById[item.id] || defaultRewardSettings(item.productId);
                return {
                  enabled:
                    settings.enabled &&
                    Boolean(item.reviewerEmail) &&
                    !item.reviewRewardCouponCode,
                  discountType: settings.discountType,
                  discountValue: Number(settings.discountValue || 0),
                  allProducts: settings.allProducts,
                  applicableProductIds: settings.allProducts
                    ? []
                    : settings.applicableProductIds,
                };
              })()
            : undefined,
      });
      const updated = result.item;
      setItems((prev) => {
        const nextStatus = String(updated.moderationStatus || "pending");
        const shouldKeep = !status || nextStatus === status;
        return prev.flatMap((row) => {
          if (row.id !== item.id) return [row];
          return shouldKeep ? [updated] : [];
        });
      });
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to update review moderation."
      );
    } finally {
      setProcessing(null);
    }
  };

  const rewardSettingsFor = (item: AdminReview) =>
    rewardById[item.id] || defaultRewardSettings(item.productId);

  const updateRewardSettings = (
    item: AdminReview,
    patch: Partial<ReviewRewardSettings>,
  ) => {
    setRewardById((prev) => ({
      ...prev,
      [item.id]: {
        ...rewardSettingsFor(item),
        ...patch,
      },
    }));
  };

  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <h1 style={{ marginTop: 0, marginBottom: 8 }}>Review Moderation</h1>
      <p style={{ marginTop: 0, color: "#4b5563" }}>
        New reviews stay hidden until approved. Flagged content is highlighted so
        your team can quickly block non-friendly submissions.
      </p>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          display: "grid",
          gap: 10,
          marginBottom: 12,
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <div style={{ fontSize: 13, color: "#374151" }}>
            <div style={{ marginBottom: 6 }}>Status</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {filterOptions.map((option) => {
                const active = status === option.value;
                return (
                  <button
                    key={`filter-${option.label}`}
                    type="button"
                    onClick={() => setStatus(option.value)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 999,
                      border: active ? "1px solid #111827" : "1px solid #e5e7eb",
                      background: active ? "#111827" : "#fff",
                      color: active ? "#fff" : "#374151",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ fontSize: 13, color: "#374151" }}>
            <div style={{ marginBottom: 6 }}>Product Category</div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <label style={{ fontSize: 13, color: "#374151", flex: 1, minWidth: 240, maxWidth: 400 }}>
            Search
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Reviewer, title, comment, order number"
              style={{
                marginTop: 6,
                width: "100%",
                padding: 8,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            />
          </label>

          <button
            onClick={load}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#111827",
              color: "white",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
          <span style={{ color: "#374151" }}>Showing: {totals.total}</span>
          <span style={{ color: "#a16207" }}>Pending: {totals.pending}</span>
          <span style={{ color: "#991b1b" }}>Flagged: {totals.flagged}</span>
        </div>
      </div>

      {error ? (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 10px",
            borderRadius: 8,
            background: "#fef2f2",
            color: "#991b1b",
            fontSize: 13,
            border: "1px solid #fecaca",
          }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <div style={{ color: "#6b7280", fontSize: 14 }}>Loading reviews...</div>
      ) : filteredItems.length === 0 ? (
        <div
          style={{
            padding: 16,
            border: "1px dashed #d1d5db",
            borderRadius: 12,
            color: "#6b7280",
            background: "#fff",
          }}
        >
          No reviews found for this filter.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {filteredItems.map((item) => {
            const currentStatus = String(item.moderationStatus || "pending");
            const isProcessing = processing?.id === item.id;
            const processingDecision = isProcessing ? processing?.decision : null;
            const isApproved = currentStatus === "approved";
            const isRejected = currentStatus === "rejected";
            const colors = statusPillColor[currentStatus] || {
              bg: "#f3f4f6",
              text: "#374151",
            };
            return (
              <div
                key={item.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  background: "#fff",
                  padding: 12,
                  display: "grid",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "#111827", fontSize: 15 }}>
                      {item.productName || "Product"}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                      Product ID: {item.productId}
                      {item.productSlug ? ` • ${item.productSlug}` : ""}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                      {item.reviewerName}
                      {item.reviewerEmail ? ` • ${item.reviewerEmail}` : ""}
                      {item.orderNumber ? ` • Order ${item.orderNumber}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ color: "#ca8a04", fontWeight: 700 }}>
                      {Math.max(1, Math.min(5, Number(item.rating || 0)))} / 5
                    </span>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: colors.bg,
                        color: colors.text,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {pretty(currentStatus)}
                    </span>
                    {item.verifiedPurchase ? (
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: "#ecfdf5",
                          color: "#065f46",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Verified Purchase
                      </span>
                    ) : null}
                  </div>
                </div>

                {item.title ? (
                  <div style={{ fontWeight: 600, color: "#1f2937" }}>{item.title}</div>
                ) : null}
                <div style={{ color: "#374151", fontSize: 14 }}>{item.comment}</div>

                {Array.isArray(item.photos) && item.photos.length > 0 ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {item.photos.slice(0, 2).map((photo, idx) => (
                      <a
                        key={`${item.id}-photo-${idx}`}
                        href={assetUrl(photo) || photo}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          width: 96,
                          height: 96,
                          overflow: "hidden",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                          background: "#f8fafc",
                          display: "block",
                        }}
                      >
                        <img
                          src={assetUrl(photo) || photo}
                          alt={`Review attachment ${idx + 1}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                ) : null}

                {Array.isArray(item.moderationFlags) && item.moderationFlags.length > 0 ? (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {item.moderationFlags.map((flag) => (
                      <span
                        key={`${item.id}-flag-${flag}`}
                        style={{
                          padding: "2px 8px",
                          borderRadius: 999,
                          border: "1px solid #fecaca",
                          background: "#fff1f2",
                          color: "#9f1239",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Flag: {flag}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    background: "#f8fafc",
                    padding: 10,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  {item.reviewRewardCouponCode ? (
                    <div style={{ color: "#166534", fontSize: 13, fontWeight: 700 }}>
                      Promo sent: {item.reviewRewardCouponCode}
                    </div>
                  ) : (
                    <>
                      <label
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={rewardSettingsFor(item).enabled}
                          disabled={!item.reviewerEmail || isApproved || isRejected}
                          onChange={(e) =>
                            updateRewardSettings(item, { enabled: e.target.checked })
                          }
                        />
                        Send promo code when approving
                      </label>
                      {!item.reviewerEmail ? (
                        <div style={{ color: "#9f1239", fontSize: 12 }}>
                          Reviewer email is missing, so a promo code email cannot be sent.
                        </div>
                      ) : null}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                          gap: 8,
                        }}
                      >
                        <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                          Discount Type
                          <select
                            value={rewardSettingsFor(item).discountType}
                            disabled={isApproved || isRejected}
                            onChange={(e) =>
                              updateRewardSettings(item, {
                                discountType:
                                  e.target.value === "fixed" ? "fixed" : "percentage",
                              })
                            }
                            style={{
                              padding: 8,
                              border: "1px solid #d1d5db",
                              borderRadius: 8,
                              background: "#fff",
                            }}
                          >
                            <option value="percentage">Percentage</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                        </label>
                        <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                          Discount Value
                          <input
                            type="number"
                            min={0}
                            step={rewardSettingsFor(item).discountType === "percentage" ? 1 : 0.01}
                            value={rewardSettingsFor(item).discountValue}
                            disabled={isApproved || isRejected}
                            onChange={(e) =>
                              updateRewardSettings(item, {
                                discountValue: e.target.value,
                              })
                            }
                            style={{
                              padding: 8,
                              border: "1px solid #d1d5db",
                              borderRadius: 8,
                              background: "#fff",
                            }}
                          />
                        </label>
                      </div>
                      <label
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          fontSize: 13,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={rewardSettingsFor(item).allProducts}
                          disabled={isApproved || isRejected}
                          onChange={(e) =>
                            updateRewardSettings(item, { allProducts: e.target.checked })
                          }
                        />
                        Promo applies to all items
                      </label>
                      {!rewardSettingsFor(item).allProducts ? (
                        <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
                          Applicable Items
                          <select
                            multiple
                            value={rewardSettingsFor(item).applicableProductIds}
                            disabled={isApproved || isRejected}
                            onChange={(e) =>
                              updateRewardSettings(item, {
                                applicableProductIds: Array.from(
                                  e.target.selectedOptions,
                                ).map((option) => option.value),
                              })
                            }
                            style={{
                              minHeight: 120,
                              padding: 8,
                              border: "1px solid #d1d5db",
                              borderRadius: 8,
                              background: "#fff",
                            }}
                          >
                            {products.map((product) => {
                              const id = String(product._id || "");
                              if (!id) return null;
                              return (
                                <option key={id} value={id}>
                                  {product.name}
                                </option>
                              );
                            })}
                          </select>
                        </label>
                      ) : null}
                    </>
                  )}
                </div>

                <div style={{ display: "grid", gap: 6 }}>
                  <textarea
                    value={notesById[item.id] ?? item.moderationNotes ?? ""}
                    onChange={(e) =>
                      setNotesById((prev) => ({ ...prev, [item.id]: e.target.value }))
                    }
                    rows={2}
                    placeholder="Moderator note (optional)"
                    style={{
                      width: "100%",
                      padding: 8,
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  />

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => moderate(item, "approved")}
                      disabled={isProcessing || isApproved}
                      style={{
                        padding: "7px 12px",
                        borderRadius: 8,
                        border: isApproved
                          ? "1px solid #16a34a"
                          : "1px solid #86efac",
                        background: isApproved ? "#16a34a" : "#f0fdf4",
                        color: isApproved ? "#fff" : "#166534",
                        fontWeight: 700,
                        cursor:
                          isProcessing || isApproved ? "not-allowed" : "pointer",
                        opacity: isProcessing || isApproved ? 0.75 : 1,
                      }}
                    >
                      {processingDecision === "approved"
                        ? "Approving..."
                        : isApproved
                          ? "Approved"
                          : "Approve"}
                    </button>
                    <button
                      type="button"
                      onClick={() => moderate(item, "rejected")}
                      disabled={isProcessing || isRejected}
                      style={{
                        padding: "7px 12px",
                        borderRadius: 8,
                        border: isRejected
                          ? "1px solid #dc2626"
                          : "1px solid #fca5a5",
                        background: isRejected ? "#dc2626" : "#fef2f2",
                        color: isRejected ? "#fff" : "#b91c1c",
                        fontWeight: 700,
                        cursor:
                          isProcessing || isRejected ? "not-allowed" : "pointer",
                        opacity: isProcessing || isRejected ? 0.75 : 1,
                      }}
                    >
                      {processingDecision === "rejected"
                        ? "Rejecting..."
                        : isRejected
                          ? "Rejected"
                          : "Reject"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
